import {
  createStudentClassRequest,
  getEduLevelSubjectsRequest,
  getEducationLevelsRequest,
  getGradeTypeSubjectsRequest,
  getGradeTypesRequest,
  getGradesRequest,
  getScheduleClassTypesRequest,
  getScheduleMediumsRequest,
  getTeacherProfileByCodeRequest,
} from '../api/studentSchedule.api';

const extractList = (payload) => {
  if (Array.isArray(payload)) return payload;
  return (
    payload?.data ||
    payload?.items ||
    payload?.results ||
    payload?.grades ||
    payload?.grade_types ||
    payload?.subjects ||
    payload?.education_levels ||
    payload?.educationLevels ||
    payload?.grade_type_subjects ||
    payload?.grade_type_grade_subjects ||
    payload?.mediums ||
    payload?.class_types ||
    []
  );
};

const pickFirst = (item, keys = []) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return null;
};

const normalizeOptions = (payload, idKeys, nameKeys, prefix) =>
  extractList(payload).map((item, index) => {
    const id = pickFirst(item, idKeys);

    return {
      id: id ?? `${prefix}-${index}`,
      name: pickFirst(item, nameKeys) || 'Unknown',
      raw: item,
    };
  });

const normalizeEducationLevels = (payload) =>
  extractList(payload).map((item, index) => ({
    id: pickFirst(item, ['id', 'education_level_id', 'educationLevelId']) ?? `education-level-${index}`,
    code: pickFirst(item, ['code', 'education_level_code', 'educationLevelCode']) || '',
    name: pickFirst(item, ['name', 'education_level_name', 'title', 'label']) || 'Unknown',
    raw: item,
  }));

const normalizeTeacher = (payload) => {
  const teacher = payload?.data || payload;

  return {
    id: pickFirst(teacher, ['id', 'teacher_id', 'user_id', 'pk']),
    code: pickFirst(teacher, ['teacher_code', 'code', 'teacherId']) || '',
    fullName:
      [pickFirst(teacher, ['first_name', 'firstName', 'given_name']), pickFirst(teacher, ['last_name', 'lastName', 'surname'])]
        .filter(Boolean)
        .join(' ') || pickFirst(teacher, ['name', 'full_name']) || 'Teacher',
    raw: teacher,
  };
};

export const fetchScheduleGrades = async () => {
  const res = await getGradesRequest();
  return normalizeOptions(res.data, ['id', 'grade_id', 'gradeId'], ['grade_name', 'name', 'title', 'grade', 'label'], 'grade');
};

export const fetchEducationLevels = async () => {
  const res = await getEducationLevelsRequest();
  return normalizeEducationLevels(res.data);
};

export const fetchScheduleGradeTypes = async (gradeId) => {
  const res = await getGradeTypesRequest(gradeId);
  return normalizeOptions(
    res.data,
    ['id', 'grade_type_grade_id', 'grade_type_id', 'gradeTypeId'],
    ['grade_type_name', 'name', 'title', 'label'],
    'grade-type',
  );
};

export const fetchScheduleSubjects = async (gradeTypeGradeId) => {
  const res = await getGradeTypeSubjectsRequest(gradeTypeGradeId);
  return normalizeOptions(res.data, ['id', 'subject_id', 'subjectId'], ['subject_name', 'name', 'title', 'label'], 'subject');
};

export const fetchEduLevelSubjects = async (educationLevelCode) => {
  const res = await getEduLevelSubjectsRequest(educationLevelCode);
  return normalizeOptions(
    res.data,
    ['education_level_subjects_id'],
    ['subject_name'],
  );
};

export const fetchScheduleMediums = async () => {
  const res = await getScheduleMediumsRequest();
  return normalizeOptions(res.data, ['id', 'medium_id', 'mediumId'], ['medium_name', 'name', 'title', 'label'], 'medium');
};

export const fetchScheduleClassTypes = async () => {
  const res = await getScheduleClassTypesRequest();
  return normalizeOptions(
    res.data,
    ['id', 'class_type_id', 'classTypeId'],
    ['class_type_name', 'name', 'title', 'label'],
    'class-type',
  );
};

export const fetchTeacherProfile = async (teacherCode) => {
  const res = await getTeacherProfileByCodeRequest(teacherCode);
  return normalizeTeacher(res.data);
};

export const createStudentClass = async (studentId, data) => {
  const res = await createStudentClassRequest(studentId, data);
  return res.data;
};
